import { ReactElement, useMemo } from "react"
import { Blockquote } from "@wfp/ui"
import { useGetPriceDataSummaryQuery } from "../../../services/marketprices/dashboard"

const PriceUploads = (): ReactElement => {
  const { startMonth, endMonth } = useMemo(() => {
    const now = new Date()
    const startDate = new Date(now.getFullYear(), now.getMonth() - 5, 1)
    const endDate = new Date(now.getFullYear(), now.getMonth(), 1)

    return {
      startMonth: startDate.getMonth() + 1,
      endMonth: endDate.getMonth() + 1,
    }
  }, [])

  const {
    data: pricesData,
    isFetching,
    isError,
    isSuccess,
  } = useGetPriceDataSummaryQuery({
    startMonth,
    endMonth,
  })

  const last6Months = useMemo((): { month: number; year: number; label: string }[] => {
    const months: { month: number; year: number; label: string }[] = []
    const now = new Date()

    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1)
      months.push({
        month: date.getMonth() + 1,
        year: date.getFullYear(),
        label: date.toLocaleDateString("en-GB", { month: "short" }),
      })
    }

    return months
  }, [pricesData])

  const tableData = useMemo(() => {
    return pricesData?.countries?.map(country => {
      const dataByMonth = Object.fromEntries(country.priceData.map(d => [d.month, d]))

      return {
        name: country.name,
        adm0Code: country.adm0Code,
        monthsData: last6Months.map(m => ({
          ...(dataByMonth[m.month] || { approved: 0, pending: 0, rejected: 0 }),
        })),
      }
    })
  }, [last6Months])

  if (isError)
    return (
      <Blockquote kind="warning" className="mx-10 text-sm mb-2 w-fit">
        Something went wrong
      </Blockquote>
    )

  if (isSuccess && !pricesData?.countries?.length)
    return (
      <Blockquote kind="info" className="mx-10 text-sm mb-2 w-fit">
        There are no price uploads
      </Blockquote>
    )

  return isFetching ? (
    <div className="flex justify-center w-full p-4 flex-col gap-2">
      <div className="h-6 bg-gray-200 rounded-lg animate-pulse" />
      <div className="h-6 bg-gray-200 rounded-lg animate-pulse" />
      <div className="h-6 bg-gray-200 rounded-lg animate-pulse" />
      <div className="h-6 bg-gray-200 rounded-lg animate-pulse" />
    </div>
  ) : (
    <div className="relative mx-8">
      <div className="overflow-auto max-h-[400px]">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className="text-left text-sm font-bold min-w-[200px] w-[200px] sticky left-0 top-0 bg-white z-30">
                Country
              </th>
              {last6Months.map(m => (
                <th
                  key={`${m.year}-${m.month}`}
                  className="text-center text-sm font-bold min-w-[100px] sticky top-0 bg-white z-20 px-3 py-2"
                >
                  <div className="text-sm">
                    {m.label} {m.year}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {tableData?.map(country => (
              <tr key={country.adm0Code} className="border-b border-gray-200 border-dotted">
                <td className="sticky left-0 bg-white font-medium text-gray-900 text-sm p-1 z-10">{country.name}</td>

                {country.monthsData.map((data, idx) => (
                  <td key={idx} className="text-center p-1">
                    <div className="inline-flex gap-1.5 text-xs">
                      {data.approved > 0 && (
                        <span
                          className="text-[var(--interactive-01)] bg-blue-200 rounded-lg px-1.5 !py-1 font-bold min-w-[24px] inline-block text-center"
                          title="Approved"
                        >
                          {data.approved}
                        </span>
                      )}
                      {data.pending > 0 && (
                        <span
                          className="text-yellow-700 bg-yellow-200 rounded-lg px-1.5 !py-1 font-bold min-w-[24px] inline-block text-center"
                          title="Pending"
                        >
                          {data.pending}
                        </span>
                      )}
                      {data.rejected > 0 && (
                        <span
                          className="text-gray-600 bg-gray-200 rounded-lg px-1.5 !py-1 font-bold min-w-[24px] inline-block text-center"
                          title="Rejected"
                        >
                          {data.rejected}
                        </span>
                      )}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex gap-2 justify-end p-2 text-xs mt-1">
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 bg-blue-200 rounded-lg" />
          <span>Approved</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 bg-yellow-200 rounded-lg" />
          <span>Pending</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 bg-gray-200 rounded-lg" />
          <span>Rejected</span>
        </div>
      </div>
    </div>
  )
}

export default PriceUploads
